import React from 'react';

import { ActionIcon, Title, Tooltip, createStyles } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { IconFilterX } from '@tabler/icons-react';
import DownloadCSV from 'components/DownloadCSV';
import KiloSelect from 'components/KiloSelect';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useCommunications from 'hooks/communications/useCommunications';
import { Notification } from 'types';

import { NOTIFICATION_TYPES, NotificationType, SearchFilters } from '../types';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xs,
    display: 'grid',
    gridTemplateColumns: 'auto auto auto min-content min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"title dateRange eventTypes clear download"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'auto auto min-content min-content',
      gridTemplateRows: 'auto auto',
      gridTemplateAreas: `
        'title title clear download'
        'dateRange eventTypes eventTypes eventTypes'
        
      `,
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'auto auto min-content min-content',
      gridTemplateRows: 'repeat(auto, 4)',
      gridTemplateAreas: `
        'title title clear download'
        'dateRange dateRange dateRange dateRange'
        'eventTypes eventTypes eventTypes eventTypes'
        
      `,
    },
  },
  title: {
    gridArea: 'title',
  },
  dateRange: {
    gridArea: 'dateRange',
    minWidth: '20rem',
  },
  eventTypes: {
    gridArea: 'eventTypes',
    minWidth: '20rem',
  },
  clear: {
    gridArea: 'clear',
  },
  download: {
    gridArea: 'download',
  },
}));

interface ICommunicationsFiltersProps {
  searchFilters: SearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  initialSearchFiltersValue: SearchFilters;
  orderingValue: string;
  timeType?: 'past' | 'upcoming';
  limit?: string;
  defaultEndDate?: string;
}

export const csvOptions = {
  fieldSeparator: ',',
  quoteStrings: '',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: false,
  headers: ['Subject', 'Date', 'Content', 'Type', 'Status'],
};

const today = new Date();

const CommunicationsFilters = ({
  searchFilters,
  setSearchFilters,
  initialSearchFiltersValue,
  orderingValue,
  defaultEndDate,
  limit,
}: ICommunicationsFiltersProps): JSX.Element => {
  const { classes } = useStyles();
  const { dateFormat } = useLocalization();

  const dataToRow = (d: Notification): { [key: string]: any } => ({
    Subject: d.contentPreview,
    Date: dayjs(d.createdAt).format(dateFormat),
    Time: dayjs(d.createdAt).format('h:mm A'),
    Content: d.contentBody,
    Type: d.notificationType,
    Status: d.deliverySuccess,
  });

  const handleDateChange = (value: DatesRangeValue): void => {
    setSearchFilters(prev => ({ ...prev, date: value }));
  };

  const handleNotificationTypeChange = (value: NotificationType): void => {
    setSearchFilters(prev => ({ ...prev, notificationType: value }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
  };

  return (
    <div className={classes.header}>
      <Title order={2} className={classes.title}>
        Communications
      </Title>
      <DatePickerInput
        className={classes.dateRange}
        value={searchFilters.date}
        onChange={handleDateChange}
        valueFormat='YYYY MMM DD'
        type='range'
        clearable
        {...{ placeholder: 'Select date range' }}
        maxDate={today}
        allowSingleDateInRange
      />
      <KiloSelect
        onChange={handleNotificationTypeChange}
        data={NOTIFICATION_TYPES}
        placeholder={'Select notification type'}
        value={searchFilters.notificationType}
      />

      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterX />
        </ActionIcon>
      </Tooltip>

      <DownloadCSV
        useList={useCommunications}
        dataToRow={dataToRow}
        hookOptions={{
          ...searchFilters,
          createdAtStart:
            searchFilters.date && searchFilters.date[0]
              ? dayjs(searchFilters.date[0]).startOf('day').toISOString()
              : undefined,
          createdAtEnd:
            searchFilters.date && searchFilters.date[1]
              ? dayjs(searchFilters.date[1]).endOf('day').toISOString()
              : defaultEndDate,

          ordering: orderingValue,
          notificationType: searchFilters.notificationType,
        }}
        limit={limit}
        csvOptions={csvOptions}
        className={classes.download}
        reportType='notifications-export'
      />
    </div>
  );
};

export default CommunicationsFilters;
